import React, { FC } from 'react';
import { Dialog, DialogTitle, DialogContent, Grid, TextField, DialogActions, Button, FormControl } from '@mui/material';
import { ApolloCache, Reference, useMutation } from '@apollo/client';
import { createGroupTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

type AddGroupDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onAddGroup: (group: { _id: string; name: string }) => void;
};
const AddGroupDialog: FC<AddGroupDialogProps> = ({ dialogOpen, onCloseDialog, onAddGroup }: AddGroupDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings', 'workflow']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [createGroup] = useMutation(createGroupTemplate, {
        onCompleted: ({ createGroup }) => {
            onAddGroup(createGroup);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<{ _id: string; name: string }>, { data: { createGroup } }) {
            cache.modify({
                fields: {
                    groups(groupRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (groupRefs instanceof Array) {
                            return [...groupRefs, toReference(createGroup)];
                        }
                        return [groupRefs, toReference(createGroup)];
                    },
                },
            });
        },
    });

    const validate = (values: { name: string }) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('workflow:VALIDATION:NAME');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            name: '',
        },
        validate,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: async (values) => {
            await createGroup({ variables: { ...values } });
            handleDialogClose();
        },
    });

    const handleDialogClose = () => {
        onCloseDialog();
        formik.resetForm();
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={dialogOpen}>
            <DialogTitle>{translate('lab-settings:ADD_GROUP')}</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    id="group-name"
                                    margin="dense"
                                    label={translate('lab-settings:GROUP_NAME')}
                                    required
                                    helperText={formik.touched.name && formik.errors.name}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    {...formik.getFieldProps('name')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>{translate('common:CANCEL')}</Button>
                    <Button variant="contained" disabled={!formik.isValid} type="submit">
                        {translate('common:SAVE')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddGroupDialog;
